import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import EventImage from "@/components/EventImage";
import { useLang } from "@/context/LanguageContext";
import { Hammer, Music, Drama, CalendarDays, Sparkles, Megaphone, MapPin, ArrowRight, Crown } from "lucide-react";

const iconFor = (type) => (type === "band" ? Music : type === "troupe" ? Drama : Hammer);

export default function ClanSpotlight() {
  const { t } = useLang();
  const { data: pros = [] } = useQuery({
    queryKey: ["clanSpotlight"],
    queryFn: async () => (await api.get("/clan/featured", { params: { limit: 6 } })).data,
  });
  const [i, setI] = useState(0);
  useEffect(() => {
    if (pros.length < 2) return;
    const id = setInterval(() => setI((v) => (v + 1) % pros.length), 5000);
    return () => clearInterval(id);
  }, [pros.length]);
  if (!pros.length) return null;

  const idx = i % pros.length;
  const e = pros[idx];
  const Icon = iconFor(e.type);
  const sponsored = e.feature_type === "sponsored";

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12" data-testid="home-clan-spotlight">
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-4 flex items-center gap-2">
        <Crown className="w-3.5 h-3.5" /> {t("clan_spotlight_tag")}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={`${e.source}-${e.slug}`}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}>
          <Link to={e.profile_path} data-testid={`clan-spotlight-${e.slug}`}
            className="group grid md:grid-cols-[minmax(0,360px)_1fr] rounded-2xl overflow-hidden border-2 border-amber-500/50 ember-glow bg-gradient-to-br from-[#181A20] via-[#121418] to-[#1E1710] hover:border-amber-400/70 transition-all">
            <div className="relative h-56 md:h-full min-h-[260px] overflow-hidden bg-stone-900">
              <EventImage src={e.image} alt={e.name} imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#121418] via-[#121418]/20 to-transparent" />
            </div>
            <div className="p-6 sm:p-8 flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-2.5 mb-3">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest font-bold ${sponsored ? "bg-stone-800/90 text-stone-200 border border-stone-600" : "bg-amber-500 text-stone-950 border border-amber-300"}`}>
                  {sponsored ? <Megaphone className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                  {sponsored ? t("dir_sponsored") : t("dir_discover")}
                </span>
                {e.craft && <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-amber-300"><Icon className="w-3.5 h-3.5" /> {e.craft}</span>}
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl text-amber-100 group-hover:text-amber-50 transition-colors mb-3">{e.name}</h3>
              <p className="text-stone-300/90 line-clamp-3 leading-relaxed mb-5 max-w-xl">{e.bio}</p>
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1.5 text-xs text-stone-400 font-mono uppercase tracking-wider"><MapPin className="w-3.5 h-3.5 text-amber-600/80" /> {e.region || "Suisse"}</span>
                {e.upcoming_events > 0 && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono bg-emerald-950/70 text-emerald-300 border border-emerald-700/50"><CalendarDays className="w-3 h-3" /> {e.upcoming_events} {t("dir_coming")}</span>}
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 group-hover:gap-2.5 transition-all ml-auto">{t("clan_see_profile")} <ArrowRight className="w-4 h-4" /></span>
              </div>
            </div>
          </Link>
        </motion.div>
      </AnimatePresence>
      {pros.length > 1 && (
        <div className="flex items-center gap-2 mt-5" data-testid="clan-spotlight-dots">
          {pros.map((_, k) => (
            <button key={k} data-testid={`clan-spotlight-dot-${k}`} onClick={() => setI(k)} aria-label={`Vedette ${k + 1}`}
              className={`h-1.5 rounded-full transition-all ${k === idx ? "w-6 bg-amber-500" : "w-1.5 bg-stone-600 hover:bg-stone-500"}`} />
          ))}
        </div>
      )}
    </section>
  );
}
